<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle pageTitle="Similarity Profiles" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-0">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 p-4 border-bottom">
          <div>
            <h5 class="mb-1 fs-16 fw-semibold text-dark">Turnitin Benchmark Profiles</h5>
            <p class="text-muted fs-13 mb-0">Kelola standar batas toleransi similaritas Turnitin dan aturan eksklusi per klasifikasi dokumen.</p>
          </div>
          <div class="d-flex align-items-center gap-3">
            <form class="position-relative table-src-form me-0" @submit.prevent>
              <input type="text" class="form-control" placeholder="Cari kode atau nama..." v-model="searchQuery" />
              <i class="material-symbols-outlined position-absolute top-50 start-0 translate-middle-y">search</i>
            </form>
            <RouterLink to="/similarity-profiles/create" class="btn btn-primary py-2 px-3 fs-14 fw-medium rounded-3">
              <span class="d-flex align-items-center gap-1">
                <i class="material-symbols-outlined fs-18">add</i>
                <span>Add New Profile</span>
              </span>
            </RouterLink>
          </div>
        </div>

        <div class="p-3 bg-light-subtle border-bottom fs-13 text-muted d-flex align-items-center gap-2">
          <i class="material-symbols-outlined fs-18 text-primary">info</i>
          <span>
            <strong>Catatan Keamanan Data:</strong> Mengubah atau menghapus profil di sini <strong>tidak akan mengubah</strong> status atau nilai protokol order yang telah masuk sebelumnya.
          </span>
        </div>

        <div class="default-table-area style-two default-table-width">
          <div class="table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th scope="col" style="width: 120px;">Urutan</th>
                  <th scope="col" style="min-width: 140px;">Profile Code</th>
                  <th scope="col" style="min-width: 220px;">Profile Name & Classification</th>
                  <th scope="col" style="min-width: 160px;">Threshold Limits</th>
                  <th scope="col" style="min-width: 180px;">Exclusion Rules</th>
                  <th scope="col" style="min-width: 110px;">Orders</th>
                  <th scope="col" style="min-width: 100px;">Status</th>
                  <th scope="col" class="text-end" style="min-width: 100px;">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="8" class="text-center py-5 text-muted">
                    <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                    Memuat data profil similaritas...
                  </td>
                </tr>
                <tr v-else-if="filteredProfiles.length === 0">
                  <td colspan="8" class="text-center py-5 text-muted">
                    Tidak ada profil similaritas yang sesuai.
                  </td>
                </tr>
                <tr v-else v-for="(profile, index) in filteredProfiles" :key="profile.id">
                  <td>
                    <div class="d-flex align-items-center gap-1">
                      <span class="badge bg-secondary-subtle text-secondary fs-12 px-2 py-1" style="min-width: 28px; text-align: center;">
                        {{ profile.sort_order ?? 0 }}
                      </span>
                      <div class="btn-group btn-group-sm ms-1" role="group">
                        <button
                          type="button"
                          class="btn btn-sm btn-light border p-0 d-flex align-items-center justify-content-center"
                          style="width: 22px; height: 22px;"
                          :disabled="index === 0 || isReordering || searchQuery !== ''"
                          @click="moveProfile(index, 'up')"
                          :title="searchQuery ? 'Hapus pencarian untuk mengubah urutan' : 'Geser ke Atas'"
                        >
                          <i class="material-symbols-outlined fs-15">arrow_upward</i>
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm btn-light border p-0 d-flex align-items-center justify-content-center"
                          style="width: 22px; height: 22px;"
                          :disabled="index === filteredProfiles.length - 1 || isReordering || searchQuery !== ''"
                          @click="moveProfile(index, 'down')"
                          :title="searchQuery ? 'Hapus pencarian untuk mengubah urutan' : 'Geser ke Bawah'"
                        >
                          <i class="material-symbols-outlined fs-15">arrow_downward</i>
                        </button>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="badge bg-secondary bg-opacity-10 text-secondary font-monospace fs-12 px-2 py-1">
                      {{ profile.profile_code }}
                    </span>
                  </td>
                  <td>
                    <div class="fw-semibold text-dark fs-14 mb-1">{{ profile.profile_name }}</div>
                    <span class="badge bg-primary bg-opacity-10 text-primary fs-11 px-2 py-1">
                      {{ profile.service_classification }}
                    </span>
                    <p v-if="profile.description" class="fs-12 text-muted mb-0 mt-1 text-truncate" style="max-width: 280px;" :title="profile.description">
                      {{ profile.description }}
                    </p>
                  </td>
                  <td>
                    <div class="fs-13">
                      <span class="text-muted">Max Overall:</span>
                      <strong class="text-dark ms-1">{{ profile.default_max_overall }}%</strong>
                    </div>
                    <div class="fs-13 mt-1">
                      <span class="text-muted">Max Single:</span>
                      <strong class="text-dark ms-1">{{ profile.default_max_single_source }}%</strong>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex flex-wrap gap-1">
                      <span class="badge fs-11 px-2 py-1" :class="profile.exclude_bibliography ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'">
                        Biblio: {{ profile.exclude_bibliography ? 'Exclude' : 'Include' }}
                      </span>
                      <span class="badge fs-11 px-2 py-1" :class="profile.exclude_quotes ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'">
                        Quotes: {{ profile.exclude_quotes ? 'Exclude' : 'Include' }}
                      </span>
                      <span class="badge bg-secondary bg-opacity-10 text-secondary fs-11 px-2 py-1">
                        Matches: {{ formatSmallMatches(profile) }}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span class="badge bg-info bg-opacity-10 text-info fs-12 px-2 py-1">
                      {{ profile.orders_count ?? 0 }} order
                    </span>
                  </td>
                  <td>
                    <span class="badge fs-12 px-2 py-1" :class="profile.is_active ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'">
                      {{ profile.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td class="text-end">
                    <div class="d-flex align-items-center justify-content-end gap-1">
                      <RouterLink :to="'/similarity-profiles/' + profile.id + '/edit'" class="btn btn-sm btn-outline-secondary p-1 border-0" title="Edit Profile">
                        <i class="material-symbols-outlined fs-18 text-body">edit</i>
                      </RouterLink>
                      <button @click="deleteProfile(profile)" class="btn btn-sm btn-outline-danger p-1 border-0" title="Delete Profile">
                        <i class="material-symbols-outlined fs-18 text-danger">delete</i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import PageTitle from '@/components/Common/PageTitle.vue';
import similarityProfileService, { SimilarityProfile } from '@/services/similarityProfileService';

export default defineComponent({
  name: 'SimilarityProfileListPage',
  components: {
    PageTitle
  },
  setup() {
    const profiles = ref<SimilarityProfile[]>([]);
    const loading = ref(true);
    const searchQuery = ref('');

    const fetchProfiles = async () => {
      loading.value = true;
      try {
        const response = await similarityProfileService.getAll({ all: 1 });
        profiles.value = response.data.data || response.data || [];
      } catch (error) {
        console.error('Error fetching similarity profiles:', error);
      } finally {
        loading.value = false;
      }
    };

    const deleteProfile = async (profile: SimilarityProfile) => {
      const count = profile.orders_count || 0;
      const confirmMsg = count > 0
        ? 'Profil "' + profile.profile_name + '" digunakan oleh ' + count + ' order. Profil akan diarsipkan secara aman (soft-delete) tanpa mengubah status atau histori order sebelumnya. Lanjutkan?'
        : 'Apakah Anda yakin ingin menghapus profil "' + profile.profile_name + '"?';

      if (confirm(confirmMsg)) {
        try {
          if (profile.id) {
            await similarityProfileService.delete(profile.id);
            await fetchProfiles();
          }
        } catch (error: any) {
          console.error('Error deleting profile:', error);
          alert(error.response?.data?.message || 'Gagal menghapus profil.');
        }
      }
    };

    const formatSmallMatches = (profile: SimilarityProfile) => {
      if (!profile.small_matches_mode || profile.small_matches_mode === 'none') {
        return 'None';
      }
      if (profile.small_matches_mode === 'words') {
        return '< ' + (profile.small_matches_value ?? 10) + ' kata';
      }
      return '< ' + (profile.small_matches_value ?? 1) + '%';
    };

    const isReordering = ref(false);

    const moveProfile = async (index: number, direction: 'up' | 'down') => {
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= filteredProfiles.value.length) return;

      isReordering.value = true;
      try {
        const list = [...filteredProfiles.value];
        const temp = list[index];
        list[index] = list[targetIndex];
        list[targetIndex] = temp;

        const updates: Promise<any>[] = [];
        list.forEach((item, idx) => {
          const newOrder = idx + 1;
          if (item.sort_order !== newOrder) {
            item.sort_order = newOrder;
            if (item.id) {
              updates.push(similarityProfileService.update(item.id, { sort_order: newOrder }));
            }
          }
        });

        await Promise.all(updates);
        await fetchProfiles();
      } catch (error) {
        console.error('Error updating similarity profile order:', error);
        alert('Gagal memperbarui urutan profil.');
        await fetchProfiles();
      } finally {
        isReordering.value = false;
      }
    };

    const filteredProfiles = computed(() => {
      let list = profiles.value;
      if (searchQuery.value) {
        const q = searchQuery.value.toLowerCase();
        list = list.filter(p =>
          (p.profile_code && p.profile_code.toLowerCase().includes(q)) ||
          (p.profile_name && p.profile_name.toLowerCase().includes(q)) ||
          (p.service_classification && p.service_classification.toLowerCase().includes(q))
        );
      }
      return [...list].sort((a, b) => 
        (Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0)) ||
        (Number(a.id ?? 0) - Number(b.id ?? 0))
      );
    });

    onMounted(() => {
      fetchProfiles();
    });

    return {
      profiles,
      loading,
      searchQuery,
      filteredProfiles,
      deleteProfile,
      formatSmallMatches,
      isReordering,
      moveProfile
    };
  }
});
</script>
